import { useCallback, useEffect, useMemo, useRef, type ReactNode } from 'react'
import { takeSnapshot } from '@/services/feedbackContext/environment'
import { getStatus } from '@/services/helpFeedback/appFeedbackApi'
import { useFeatureStore } from '@/stores/featureStore'
import { useHelpFeedbackBridge } from '@/stores/helpFeedbackBridge'
import { useHelpFeedbackStore } from '@/stores/helpFeedbackStore'
import { useTourStore } from '@/stores/tourStore'
import type { AnyTour } from '@/services/tour/tourModel'
import { isResumable, startConversation, type Opener } from './conversation'
import { HelpFeedbackContext, PanelControlsContext } from './helpFeedbackContext'
import { HelpFeedbackPanel } from './HelpFeedbackPanel'
import { AskTab } from './AskTab'
import { isHelpShortcut } from './shortcut'
import { YourFeedbackTab } from './YourFeedbackTab'

/**
 * The shell's one Help & feedback panel (SA-1310). Every entry point opens it through
 * `useHelpFeedback()`; it stays mounted while offered, so closing keeps the conversation for a resume.
 */
export function HelpFeedbackHost({ children }: { children: ReactNode }) {
  const available = useFeatureStore((s) => s.isLoaded && s.features.has('product_feedback'))
  const open = useHelpFeedbackStore((s) => s.open)
  // The opener may be a menu item that is gone by the time the panel closes.
  const returnTo = useRef<HTMLElement | null>(null)
  const lastOpener = useRef<Opener>({})
  const escape = useRef<(() => boolean) | null>(null)

  const startFresh = useCallback(() => {
    const store = useHelpFeedbackStore.getState()
    store.start(startConversation(lastOpener.current, takeSnapshot(), Date.now()))
    store.setOfferResume(false)
  }, [])

  const openPanel = useCallback((opener: Opener = {}) => {
    // Opening the panel ends a tour (SA-1315 §4); one the panel started brings it back as it was.
    const tour = useTourStore.getState()
    if (tour.tour) {
      const fromPanel = tour.origin === 'panel'
      tour.exit()
      if (fromPanel) return
    }
    const active = document.activeElement
    returnTo.current = active instanceof HTMLElement && active !== document.body ? active : null
    lastOpener.current = opener
    const store = useHelpFeedbackStore.getState()
    // An unsent conversation with the person's words is kept for the resume offer.
    const resumable = isResumable(store.conversation)
    if (!resumable) startFresh()
    store.setOfferResume(resumable)
    store.setTab('ask')
    store.setOpen(true)
  }, [startFresh])

  const close = useCallback(() => {
    escape.current = null
    useHelpFeedbackStore.getState().setOpen(false)
  }, [])

  const onClosed = useCallback(() => {
    // Folded for a tour: the card takes the focus, and the panel's opener waits for its real close.
    if (useTourStore.getState().origin === 'panel') return
    const target = returnTo.current
    returnTo.current = null
    if (target?.isConnected) target.focus()
  }, [])

  // Code above the host (the toasts) reaches it through the bridge.
  useEffect(() => {
    const bridge = useHelpFeedbackBridge.getState()
    bridge.setHostReady(available)
    return () => useHelpFeedbackBridge.getState().setHostReady(false)
  }, [available])

  const pendingOpen = useHelpFeedbackBridge((s) => s.pendingOpen)
  useEffect(() => {
    if (!available || !pendingOpen) return
    const opener = useHelpFeedbackBridge.getState().consumePendingOpen()
    if (opener) openPanel(opener)
  }, [available, pendingOpen, openPanel])

  // `?` opens the panel from anywhere but a field (SA-1306 spec §2.3).
  useEffect(() => {
    if (!available || open) return
    const onKey = (event: KeyboardEvent) => {
      if (!isHelpShortcut(event)) return
      event.preventDefault()
      openPanel()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [available, open, openPanel])

  // The assistant's status, read each time the panel opens: a model or a setting an admin saves
  // while this tab is open takes effect on the next open, with no reload. The last answer stands
  // while the read is out, and a failed read keeps it.
  useEffect(() => {
    if (!open) return
    let live = true
    getStatus()
      .then((s) => {
        if (!live) return
        const store = useHelpFeedbackStore.getState()
        store.setOffers({ answers: s.answers ?? true, voice: s.voice ?? true })
        store.setStatus(s.assistant ? 'available' : 'unavailable')
      })
      .catch(() => {})
    return () => {
      live = false
    }
  }, [open])

  // A tour started from the panel folds it to the tour's chip, and the panel comes back after,
  // with the focus on the answer's Yes when "Show me" started it (SA-1316).
  const afterTour = useRef<number | null>(null)
  const startTour = useCallback((tour: AnyTour, returnTo?: number) => {
    afterTour.current = returnTo ?? null
    useHelpFeedbackStore.getState().setOpen(false)
    useTourStore.getState().start(tour, 'panel')
  }, [])
  const focusOnOpen = useCallback(() => {
    const id = afterTour.current
    afterTour.current = null
    if (id === null) return null
    return document.querySelector<HTMLElement>(`[data-help-yes="${id}"]`)
  }, [])
  useEffect(
    () =>
      useTourStore.subscribe((now, was) => {
        if (was.origin === 'panel' && now.tour === null) useHelpFeedbackStore.getState().setOpen(true)
      }),
    []
  )

  const value = useMemo(() => ({ available, open: openPanel }), [available, openPanel])
  const controls = useMemo(() => ({ close, startFresh, escape, startTour }), [close, startFresh, startTour])

  return (
    <HelpFeedbackContext.Provider value={value}>
      {children}
      {available && (
        <PanelControlsContext.Provider value={controls}>
          <HelpFeedbackPanel
            open={open}
            ask={<AskTab />}
            mine={<YourFeedbackTab />}
            onClosed={onClosed}
            focusOnOpen={focusOnOpen}
          />
        </PanelControlsContext.Provider>
      )}
    </HelpFeedbackContext.Provider>
  )
}
